// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';

// material ui
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

// feature-components
import ActiveHoursTooltip from '@/features/energyxplorer/components/card/ActiveHoursTooltip';

// feature-hooks
import { useEnergyGroupOpeningHours } from '@/features/energyxplorer/hooks/useEnergyGroupOpeningHours';
import { useSeriesFiltersParam } from '@/features/energyxplorer/hooks/useSeriesFilterParam';

const EnergyAnalyzerFilterDialog = ({
  open,
  handleClose,
  seriesFilterParam,
}) => {
  // TODO: this component is used both in ChildAnalysisTab and EnergyTreeNodeAnalysisPage but they use different URL params
  const { addSeries } = useSeriesFiltersParam(seriesFilterParam);
  const [activePeriodFilter, setActivePeriodFilter] = useState(undefined);
  const [holidaysFilter, setHolidaysFilter] = useState(undefined);
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const { openingHours } = useEnergyGroupOpeningHours();

  const handleSubmit = () => {
    // Push new series to URL; ChildAnalysisTab will refetch via effect
    addSeries({
      activePeriodFilter: activePeriodFilter,
      activeHolidaysFilter: holidaysFilter,
    });
    onClose();
  };

  const onClose = () => {
    setActivePeriodFilter(undefined);
    setHolidaysFilter(undefined);
    handleClose();
  };

  const renderToggleGroup = (label, value, setValue, options) => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={stringify(value)}
        exclusive
        onChange={(_, newValue) => setValue(parseNullable(newValue))}
        fullWidth
        color="primary"
      >
        {options.map(({ label, value, tooltip }) => (
          <Tooltip key={value} title={tooltip || ''} placement="bottom">
            <ToggleButton
              key={value}
              value={value}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {label}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('child_analysis.add_filtered_series')}</DialogTitle>
      <DialogContent dividers>
        {renderToggleGroup(
          t('filter_labels.active_period'),
          activePeriodFilter,
          setActivePeriodFilter,
          [
            { label: 'Alle', value: '' },
            {
              label: t('filter_labels.active_period'),
              value: 'true',
              tooltip: <ActiveHoursTooltip openingHours={openingHours} />,
            },
            {
              label: t('filter_labels.inactive_period'),
              value: 'false',
              tooltip: (
                <ActiveHoursTooltip
                  openingHours={openingHours}
                  inverted={true}
                />
              ),
            },
          ],
        )}

        {renderToggleGroup(
          t('filter_labels.holidays'),
          holidaysFilter,
          setHolidaysFilter,
          [
            { label: 'Alle', value: '' },
            { label: 'Kun helligdage', value: 'true' },
            { label: 'Ingen helligdage', value: 'false' },
          ],
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant="text" onClick={onClose}>
          {t('buttonLabel.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          endIcon={<AddRoundedIcon />}
        >
          {t('buttonLabel.add_filtered_series')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const parseNullable = (val) => {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
};

const stringify = (val) => {
  if (val === true) return 'true';
  if (val === false) return 'false';
  return '';
};

export default EnergyAnalyzerFilterDialog;
